import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface TournamentGroup {
    TORNEO_CLAVE: number;
    TORNEO_NOMBRE: string;
    turnos: TurnoInfo[];
}

interface TurnoInfo {
    TURNO_CLAVE: number;
    TURNO_NOME: string;
    EVENTO_CLAVE: number;
    EVENTO_NOME: string;
    TEAMBOWLERS: number;
}

interface EventBowler {
    CLAVE: number;
    FICHANAL: string | number;
    NOMBRECOMPLETO: string;
    ON_TEAM: number;
}

interface TeamMember {
    ORDEN: number;
    JUGADOR_CLAVE: number;
    NOMBRECOMPLETO: string;
    FICHANAL: string | number;
}

interface AdminTeam {
    CLAVE: number;
    NOMBRE: string;
    members: TeamMember[];
}

const PAGE_SIZE = 15;

const AdminManageTeams = () => {
    const navigate = useNavigate();
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

    // Phase 1 — tournament selection
    const [tournaments, setTournaments] = useState<TournamentGroup[]>([]);
    const [loadingTournaments, setLoadingTournaments] = useState(true);
    const [selectedTournament, setSelectedTournament] = useState<TournamentGroup | null>(null);

    // Phase 2 — turno tabs
    const [selectedTurnoIdx, setSelectedTurnoIdx] = useState(0);

    // Left panel — bowler list
    const [bowlers, setBowlers] = useState<EventBowler[]>([]);
    const [bowlerTotal, setBowlerTotal] = useState(0);
    const [bowlerPage, setBowlerPage] = useState(1);
    const [bowlerSearch, setBowlerSearch] = useState('');
    const [bowlerLoading, setBowlerLoading] = useState(false);

    // Right panel — all teams
    const [teams, setTeams] = useState<AdminTeam[]>([]);
    const [teamsLoading, setTeamsLoading] = useState(false);

    // Add target: null = view only, 'new' = creating new team, number = adding to existing team
    const [addTarget, setAddTarget] = useState<number | 'new' | null>(null);

    // New team creation form
    const [teamName, setTeamName] = useState('');
    const [pendingMembers, setPendingMembers] = useState<EventBowler[]>([]);
    const [saveLoading, setSaveLoading] = useState(false);
    const [saveError, setSaveError] = useState('');

    // Member actions
    const [memberActionLoading, setMemberActionLoading] = useState<number | null>(null);
    const [memberActionError, setMemberActionError] = useState('');

    // ── Auth check ────────────────────────────────────────────────────────────
    useEffect(() => {
        const adminStr = localStorage.getItem('loggedInAdmin');
        if (!adminStr) { navigate('/admin'); return; }

        fetch(`${BACKEND_URL}/api/teams/admin-tournaments`)
            .then(r => r.json())
            .then((rows: any[]) => {
                const map: Record<number, TournamentGroup> = {};
                for (const row of rows) {
                    if (!map[row.TORNEO_CLAVE]) {
                        map[row.TORNEO_CLAVE] = {
                            TORNEO_CLAVE: row.TORNEO_CLAVE,
                            TORNEO_NOMBRE: row.TORNEO_NOMBRE,
                            turnos: []
                        };
                    }
                    map[row.TORNEO_CLAVE].turnos.push({
                        TURNO_CLAVE: row.TURNO_CLAVE,
                        TURNO_NOME: row.TURNO_NOME,
                        EVENTO_CLAVE: row.EVENTO_CLAVE,
                        EVENTO_NOME: row.EVENTO_NOMBRE,
                        TEAMBOWLERS: row.TEAMBOWLERS
                    });
                }
                setTournaments(Object.values(map));
            })
            .catch(console.error)
            .finally(() => setLoadingTournaments(false));
    }, [BACKEND_URL, navigate]);

    // ── Derived ───────────────────────────────────────────────────────────────
    const selectedTurno = selectedTournament?.turnos[selectedTurnoIdx] ?? null;
    const totalPages = Math.ceil(bowlerTotal / PAGE_SIZE);

    const targetTeamData = typeof addTarget === 'number'
        ? teams.find(t => t.CLAVE === addTarget) ?? null
        : null;

    const targetTeamFull = targetTeamData != null &&
        selectedTurno != null &&
        targetTeamData.members.length >= selectedTurno.TEAMBOWLERS;

    const pendingFull = selectedTurno != null && pendingMembers.length >= selectedTurno.TEAMBOWLERS;

    const isPending = (clave: number) => pendingMembers.some(m => m.CLAVE === clave);
    const isOnTargetTeam = (clave: number) =>
        targetTeamData?.members.some(m => m.JUGADOR_CLAVE === clave) ?? false;

    // ── Fetch helpers ─────────────────────────────────────────────────────────
    const fetchBowlers = (torneoClave: number, turnoClave: number, search: string, page: number) => {
        setBowlerLoading(true);
        fetch(
            `${BACKEND_URL}/api/teams/event-bowlers/${torneoClave}/${turnoClave}` +
            `?search=${encodeURIComponent(search)}&page=${page}&pageSize=${PAGE_SIZE}`
        )
            .then(r => r.json())
            .then(data => { setBowlers(data.bowlers || []); setBowlerTotal(data.total || 0); })
            .catch(console.error)
            .finally(() => setBowlerLoading(false));
    };

    const fetchTeams = (torneoClave: number, turnoClave: number) => {
        setTeamsLoading(true);
        fetch(`${BACKEND_URL}/api/teams/all-teams/${torneoClave}/${turnoClave}`)
            .then(r => r.json())
            .then((data: AdminTeam[]) => setTeams(data || []))
            .catch(console.error)
            .finally(() => setTeamsLoading(false));
    };

    // ── Reload when turno tab changes ─────────────────────────────────────────
    useEffect(() => {
        if (!selectedTournament) return;
        const turno = selectedTournament.turnos[selectedTurnoIdx];
        if (!turno) return;

        setAddTarget(null);
        setTeamName('');
        setPendingMembers([]);
        setSaveError('');
        setBowlerSearch('');
        setBowlerPage(1);
        setMemberActionError('');

        fetchBowlers(selectedTournament.TORNEO_CLAVE, turno.TURNO_CLAVE, '', 1);
        fetchTeams(selectedTournament.TORNEO_CLAVE, turno.TURNO_CLAVE);
    }, [selectedTournament, selectedTurnoIdx]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Handlers ──────────────────────────────────────────────────────────────
    const handleSearchChange = (val: string) => {
        if (!selectedTournament || !selectedTurno) return;
        setBowlerSearch(val);
        setBowlerPage(1);
        fetchBowlers(selectedTournament.TORNEO_CLAVE, selectedTurno.TURNO_CLAVE, val, 1);
    };

    const handlePageChange = (p: number) => {
        if (!selectedTournament || !selectedTurno) return;
        setBowlerPage(p);
        fetchBowlers(selectedTournament.TORNEO_CLAVE, selectedTurno.TURNO_CLAVE, bowlerSearch, p);
    };

    const addToPending = (b: EventBowler) => {
        if (!selectedTurno || pendingFull || isPending(b.CLAVE)) return;
        setSaveError('');
        setPendingMembers(prev => [...prev, b]);
    };

    const removeFromPending = (clave: number) => {
        setPendingMembers(prev => prev.filter(m => m.CLAVE !== clave));
        setSaveError('');
    };

    const addMemberToTeam = async (teamClave: number, bowler: EventBowler) => {
        if (!selectedTournament || !selectedTurno) return;
        setMemberActionLoading(bowler.CLAVE);
        setMemberActionError('');
        try {
            const res = await fetch(`${BACKEND_URL}/api/teams/add-member`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    teamClave,
                    torneoClave: selectedTournament.TORNEO_CLAVE,
                    turnoClave: selectedTurno.TURNO_CLAVE,
                    jugadorClave: bowler.CLAVE
                })
            });
            const data = await res.json();
            if (res.ok && data.success) {
                fetchTeams(selectedTournament.TORNEO_CLAVE, selectedTurno.TURNO_CLAVE);
                fetchBowlers(selectedTournament.TORNEO_CLAVE, selectedTurno.TURNO_CLAVE, bowlerSearch, bowlerPage);
            } else {
                setMemberActionError(data.error || 'Failed to add bowler');
            }
        } catch {
            setMemberActionError('An error occurred. Please try again.');
        } finally {
            setMemberActionLoading(null);
        }
    };

    const removeMemberFromTeam = async (teamClave: number, jugadorClave: number) => {
        if (!selectedTournament || !selectedTurno) return;
        setMemberActionLoading(jugadorClave);
        setMemberActionError('');
        try {
            const res = await fetch(`${BACKEND_URL}/api/teams/remove-member`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ teamClave, jugadorClave })
            });
            const data = await res.json();
            if (res.ok && data.success) {
                if (data.teamDeleted && addTarget === teamClave) {
                    setAddTarget(null);
                }
                fetchTeams(selectedTournament.TORNEO_CLAVE, selectedTurno.TURNO_CLAVE);
                fetchBowlers(selectedTournament.TORNEO_CLAVE, selectedTurno.TURNO_CLAVE, bowlerSearch, bowlerPage);
            } else {
                setMemberActionError(data.error || 'Failed to remove bowler');
            }
        } catch {
            setMemberActionError('An error occurred. Please try again.');
        } finally {
            setMemberActionLoading(null);
        }
    };

    const handleCreateTeam = async () => {
        if (!selectedTournament || !selectedTurno) return;
        if (!teamName.trim()) { setSaveError('Please enter a team name'); return; }
        if (pendingMembers.length === 0) { setSaveError('Please add at least one bowler'); return; }

        setSaveLoading(true);
        setSaveError('');
        try {
            const res = await fetch(`${BACKEND_URL}/api/teams/admin-create`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    torneoClave: selectedTournament.TORNEO_CLAVE,
                    eventoClave: selectedTurno.EVENTO_CLAVE,
                    turnoClave: selectedTurno.TURNO_CLAVE,
                    teamName: teamName.trim(),
                    memberClaves: pendingMembers.map(m => m.CLAVE)
                })
            });
            const data = await res.json();
            if (res.ok && data.success) {
                setAddTarget(null);
                setTeamName('');
                setPendingMembers([]);
                fetchTeams(selectedTournament.TORNEO_CLAVE, selectedTurno.TURNO_CLAVE);
                fetchBowlers(selectedTournament.TORNEO_CLAVE, selectedTurno.TURNO_CLAVE, bowlerSearch, bowlerPage);
            } else {
                setSaveError(data.error || 'Failed to create team');
            }
        } catch {
            setSaveError('An error occurred. Please try again.');
        } finally {
            setSaveLoading(false);
        }
    };

    const handleSelectTournament = (t: TournamentGroup) => {
        setSelectedTournament(t);
        setSelectedTurnoIdx(0);
    };

    const handleBack = () => {
        setSelectedTournament(null);
        setSelectedTurnoIdx(0);
        setBowlers([]);
        setTeams([]);
        setAddTarget(null);
    };

    // ── Loading ───────────────────────────────────────────────────────────────
    if (loadingTournaments) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 font-medium">Loading team events...</p>
                </div>
            </div>
        );
    }

    // ── Phase 1: Tournament selection ─────────────────────────────────────────
    if (!selectedTournament) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
                <div className="bg-white shadow-sm border-b border-gray-200">
                    <div className="max-w-6xl mx-auto px-4 py-5 flex items-center gap-4">
                        <button
                            onClick={() => navigate('/admin/dashboard')}
                            className="text-gray-500 hover:text-gray-700 transition"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                        </button>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Manage Teams</h1>
                            <p className="text-sm text-gray-500">Select a tournament to manage all teams</p>
                        </div>
                    </div>
                </div>

                <div className="max-w-6xl mx-auto px-4 py-10">
                    {tournaments.length === 0 ? (
                        <div className="text-center py-24">
                            <div className="bg-white rounded-2xl shadow-lg p-12 inline-block">
                                <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <p className="text-gray-500 font-semibold text-lg mb-2">No team events found</p>
                                <p className="text-gray-400 text-sm">There are no tournaments with team events configured.</p>
                                <button
                                    onClick={() => navigate('/admin/dashboard')}
                                    className="mt-6 bg-indigo-600 text-white px-6 py-2.5 rounded-xl font-semibold hover:bg-indigo-700 transition"
                                >
                                    Back to Dashboard
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {tournaments.map(t => (
                                <button
                                    key={t.TORNEO_CLAVE}
                                    onClick={() => handleSelectTournament(t)}
                                    className="bg-white rounded-2xl shadow-lg p-6 text-left hover:shadow-xl transition transform hover:-translate-y-1 group border-2 border-transparent hover:border-indigo-200"
                                >
                                    <div className="bg-indigo-50 rounded-xl p-3 inline-block mb-4 group-hover:scale-110 transition">
                                        <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                    </div>
                                    <h3 className="text-lg font-bold text-gray-800 mb-3">{t.TORNEO_NOMBRE}</h3>
                                    <div className="flex flex-wrap gap-2">
                                        {t.turnos.map(turno => (
                                            <span
                                                key={turno.TURNO_CLAVE}
                                                className="text-xs bg-indigo-100 text-indigo-700 px-2.5 py-1 rounded-full font-semibold"
                                            >
                                                {turno.EVENTO_NOME} — {turno.TURNO_NOME} · {turno.TEAMBOWLERS} bowlers
                                            </span>
                                        ))}
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // ── Phase 2: Team management ──────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
            {/* Header */}
            <div className="bg-white shadow-sm border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-4 py-5 flex items-center gap-4">
                    <button onClick={handleBack} className="text-gray-500 hover:text-gray-700 transition">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Manage Teams</h1>
                        <p className="text-sm text-gray-500">{selectedTournament.TORNEO_NOMBRE}</p>
                    </div>
                </div>
            </div>

            {/* Turno tabs */}
            {selectedTournament.turnos.length > 1 && (
                <div className="bg-white border-b border-gray-200 shadow-sm">
                    <div className="max-w-7xl mx-auto px-4">
                        <div className="flex overflow-x-auto">
                            {selectedTournament.turnos.map((turno, idx) => (
                                <button
                                    key={turno.TURNO_CLAVE}
                                    onClick={() => setSelectedTurnoIdx(idx)}
                                    className={`px-5 py-3.5 text-sm font-semibold border-b-2 transition whitespace-nowrap ${
                                        selectedTurnoIdx === idx
                                            ? 'border-indigo-600 text-indigo-700'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                                >
                                    {turno.EVENTO_NOME} — {turno.TURNO_NOME}
                                    <span className="ml-2 text-xs font-normal text-gray-400">({turno.TEAMBOWLERS} max)</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Main two-panel layout */}
            <div className="max-w-7xl mx-auto px-4 py-6">
                {selectedTournament.turnos.length === 1 && selectedTurno && (
                    <div className="mb-4 flex items-center gap-2">
                        <span className="text-sm font-semibold text-indigo-700 bg-indigo-100 px-3 py-1 rounded-full">
                            {selectedTurno.EVENTO_NOME} — {selectedTurno.TURNO_NOME} · {selectedTurno.TEAMBOWLERS} bowlers max
                        </span>
                    </div>
                )}

                <div className="flex gap-6 items-start">
                    {/* ── Left: Bowler list ──────────────────────────────────── */}
                    <div className="w-80 flex-shrink-0">
                        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                            {/* Mode banner */}
                            {addTarget !== null && (
                                <div className={`px-4 py-2 text-xs font-semibold flex items-center gap-2 ${
                                    addTarget === 'new'
                                        ? 'bg-emerald-50 text-emerald-700 border-b border-emerald-100'
                                        : 'bg-indigo-50 text-indigo-700 border-b border-indigo-100'
                                }`}>
                                    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                    </svg>
                                    {addTarget === 'new'
                                        ? 'Adding to new team — click + to add'
                                        : `Adding to: ${targetTeamData?.NOMBRE ?? '...'}`
                                    }
                                </div>
                            )}

                            <div className="px-4 py-4 border-b border-gray-100">
                                <h2 className="font-bold text-gray-800 mb-3 text-sm uppercase tracking-wide">
                                    {selectedTurno?.TURNO_NOME} Bowlers
                                </h2>
                                <input
                                    type="text"
                                    placeholder="Search name or TAT#..."
                                    value={bowlerSearch}
                                    onChange={e => handleSearchChange(e.target.value)}
                                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition"
                                />
                            </div>

                            <div className="overflow-y-auto divide-y divide-gray-50" style={{ maxHeight: '520px' }}>
                                {bowlerLoading ? (
                                    <div className="flex items-center justify-center py-12">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                                    </div>
                                ) : bowlers.length === 0 ? (
                                    <p className="text-center text-gray-400 py-10 text-sm">No bowlers found</p>
                                ) : (
                                    bowlers.map(b => {
                                        const onAnyTeam = b.ON_TEAM > 0;
                                        const onTarget = isOnTargetTeam(b.CLAVE);
                                        const inPending = isPending(b.CLAVE);
                                        const isLoading = memberActionLoading === b.CLAVE;

                                        return (
                                            <div key={b.CLAVE} className="flex items-center px-4 py-3 gap-3 hover:bg-gray-50 transition">
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-semibold text-gray-800 text-sm truncate">{b.NOMBRECOMPLETO}</p>
                                                    <p className="text-xs text-gray-400">TAT# {b.FICHANAL}</p>
                                                </div>

                                                {isLoading ? (
                                                    /* Spinner */
                                                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center">
                                                        <svg className="w-3.5 h-3.5 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                                        </svg>
                                                    </span>

                                                ) : addTarget === null ? (
                                                    /* View-only mode — show on-team badge */
                                                    onAnyTeam ? (
                                                        <span title="On a team" className="flex-shrink-0 w-7 h-7 rounded-full bg-green-100 flex items-center justify-center">
                                                            <svg className="w-3.5 h-3.5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                            </svg>
                                                        </span>
                                                    ) : (
                                                        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-50 flex items-center justify-center">
                                                            <svg className="w-3.5 h-3.5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                            </svg>
                                                        </span>
                                                    )

                                                ) : addTarget === 'new' ? (
                                                    /* New team mode */
                                                    onAnyTeam ? (
                                                        <span title="Already on a team" className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center">
                                                            <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                                                            </svg>
                                                        </span>
                                                    ) : inPending ? (
                                                        <button
                                                            onClick={() => removeFromPending(b.CLAVE)}
                                                            title="Remove from new team"
                                                            className="flex-shrink-0 w-7 h-7 rounded-full bg-green-100 hover:bg-red-100 flex items-center justify-center transition group"
                                                        >
                                                            <svg className="w-3.5 h-3.5 text-green-600 group-hover:text-red-500 transition" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                            </svg>
                                                        </button>
                                                    ) : pendingFull ? (
                                                        <span title="Team is full" className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center">
                                                            <svg className="w-3.5 h-3.5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                            </svg>
                                                        </span>
                                                    ) : (
                                                        <button
                                                            onClick={() => addToPending(b)}
                                                            title="Add to new team"
                                                            className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-100 hover:bg-emerald-200 flex items-center justify-center transition"
                                                        >
                                                            <svg className="w-3.5 h-3.5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                                                            </svg>
                                                        </button>
                                                    )

                                                ) : (
                                                    /* Adding to existing team mode */
                                                    onTarget ? (
                                                        <span title="On this team" className="flex-shrink-0 w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center">
                                                            <svg className="w-3.5 h-3.5 text-indigo-500" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                            </svg>
                                                        </span>
                                                    ) : onAnyTeam ? (
                                                        <span title="On another team" className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center">
                                                            <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                                                            </svg>
                                                        </span>
                                                    ) : targetTeamFull ? (
                                                        <span title="Team is full" className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center">
                                                            <svg className="w-3.5 h-3.5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                            </svg>
                                                        </span>
                                                    ) : (
                                                        <button
                                                            onClick={() => addMemberToTeam(addTarget as number, b)}
                                                            title="Add to team"
                                                            className="flex-shrink-0 w-7 h-7 rounded-full bg-indigo-100 hover:bg-indigo-200 flex items-center justify-center transition"
                                                        >
                                                            <svg className="w-3.5 h-3.5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                                                            </svg>
                                                        </button>
                                                    )
                                                )}
                                            </div>
                                        );
                                    })
                                )}
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
                                    <button
                                        onClick={() => handlePageChange(bowlerPage - 1)}
                                        disabled={bowlerPage <= 1}
                                        className="text-sm text-indigo-600 hover:underline disabled:text-gray-300 disabled:no-underline"
                                    >
                                        Previous
                                    </button>
                                    <span className="text-xs text-gray-400">{bowlerPage} / {totalPages}</span>
                                    <button
                                        onClick={() => handlePageChange(bowlerPage + 1)}
                                        disabled={bowlerPage >= totalPages}
                                        className="text-sm text-indigo-600 hover:underline disabled:text-gray-300 disabled:no-underline"
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ── Right: Teams panel ─────────────────────────────────── */}
                    <div className="flex-1 min-w-0">

                        {/* Action bar */}
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <h2 className="text-lg font-bold text-gray-800">
                                    Teams
                                </h2>
                                {!teamsLoading && (
                                    <span className="text-xs font-bold bg-indigo-100 text-indigo-700 px-2.5 py-1 rounded-full">
                                        {teams.length} team{teams.length !== 1 ? 's' : ''}
                                    </span>
                                )}
                            </div>
                            <button
                                onClick={() => setAddTarget(addTarget === 'new' ? null : 'new')}
                                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition ${
                                    addTarget === 'new'
                                        ? 'bg-green-600 text-white hover:bg-green-700'
                                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                                }`}
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                                </svg>
                                {addTarget === 'new' ? 'Cancel New Team' : 'New Team'}
                            </button>
                        </div>

                        {/* Error banner */}
                        {memberActionError && (
                            <div className="mb-4 flex items-start gap-2 text-red-700 text-sm bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                                <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                {memberActionError}
                            </div>
                        )}

                        {/* New team create form */}
                        {addTarget === 'new' && (
                            <div className="bg-white rounded-2xl shadow-lg p-6 mb-4 ring-2 ring-emerald-400">
                                <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
                                    <span className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center">
                                        <svg className="w-3.5 h-3.5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                                        </svg>
                                    </span>
                                    Create New Team
                                    <span className="text-xs font-normal text-gray-400 ml-1">
                                        {selectedTurno?.EVENTO_NOME} — {selectedTurno?.TURNO_NOME} · up to {selectedTurno?.TEAMBOWLERS} bowlers
                                    </span>
                                </h3>

                                <div className="mb-4">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Team Name</label>
                                    <input
                                        type="text"
                                        placeholder="Enter team name..."
                                        value={teamName}
                                        onChange={e => { setTeamName(e.target.value); setSaveError(''); }}
                                        className="w-full border-2 border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 transition"
                                    />
                                </div>

                                {/* Pending members */}
                                <div className="mb-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-semibold text-gray-700">Members</span>
                                        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                                            selectedTurno && pendingMembers.length === selectedTurno.TEAMBOWLERS
                                                ? 'bg-green-100 text-green-700'
                                                : 'bg-gray-100 text-gray-500'
                                        }`}>
                                            {pendingMembers.length} / {selectedTurno?.TEAMBOWLERS}
                                        </span>
                                    </div>

                                    {pendingMembers.length === 0 ? (
                                        <div className="border-2 border-dashed border-emerald-200 rounded-xl py-6 text-center">
                                            <p className="text-gray-400 text-sm">Use the + buttons on the left to add bowlers</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            {pendingMembers.map((m, idx) => (
                                                <div key={m.CLAVE} className="flex items-center gap-3 bg-emerald-50 rounded-xl px-3 py-2.5">
                                                    <div className="w-7 h-7 rounded-full bg-emerald-200 flex items-center justify-center text-emerald-700 font-bold text-xs flex-shrink-0">
                                                        {idx + 1}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="font-semibold text-gray-800 text-sm truncate">{m.NOMBRECOMPLETO}</p>
                                                        <p className="text-xs text-gray-400">TAT# {m.FICHANAL}</p>
                                                    </div>
                                                    <button
                                                        onClick={() => removeFromPending(m.CLAVE)}
                                                        className="flex-shrink-0 text-gray-400 hover:text-red-500 transition"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {saveError && (
                                    <div className="mb-3 text-red-700 text-sm bg-red-50 border border-red-200 rounded-xl px-4 py-2.5">
                                        {saveError}
                                    </div>
                                )}

                                <div className="flex gap-2">
                                    <button
                                        onClick={handleCreateTeam}
                                        disabled={saveLoading || !teamName.trim() || pendingMembers.length === 0}
                                        className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2.5 rounded-xl font-bold text-sm transition disabled:cursor-not-allowed"
                                    >
                                        {saveLoading ? 'Saving...' : 'Save Team'}
                                    </button>
                                    <button
                                        onClick={() => { setAddTarget(null); setTeamName(''); setPendingMembers([]); setSaveError(''); }}
                                        className="px-4 py-2.5 rounded-xl font-semibold text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 transition"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Teams list */}
                        {teamsLoading ? (
                            <div className="bg-white rounded-2xl shadow-lg p-10 flex items-center justify-center min-h-[200px]">
                                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
                            </div>
                        ) : teams.length === 0 && addTarget !== 'new' ? (
                            <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
                                <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <p className="text-gray-500 font-semibold mb-1">No teams yet</p>
                                <p className="text-gray-400 text-sm mb-4">Click "+ New Team" to create the first team for this squad.</p>
                                <button
                                    onClick={() => setAddTarget('new')}
                                    className="bg-emerald-600 text-white px-5 py-2 rounded-xl font-semibold text-sm hover:bg-emerald-700 transition"
                                >
                                    Create First Team
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {teams.map(team => {
                                    const isTarget = addTarget === team.CLAVE;
                                    const isFull = selectedTurno != null && team.members.length >= selectedTurno.TEAMBOWLERS;

                                    return (
                                        <div
                                            key={team.CLAVE}
                                            className={`bg-white rounded-2xl shadow-lg p-5 transition ${
                                                isTarget ? 'ring-2 ring-indigo-500' : ''
                                            }`}
                                        >
                                            {/* Team header */}
                                            <div className="flex items-center justify-between mb-4">
                                                <div className="flex items-center gap-3 min-w-0">
                                                    <div className="bg-indigo-100 rounded-xl p-2 flex-shrink-0">
                                                        <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                                                        </svg>
                                                    </div>
                                                    <h3 className="font-bold text-gray-900 truncate">{team.NOMBRE}</h3>
                                                    <span className={`flex-shrink-0 text-xs font-bold px-2.5 py-1 rounded-full ${
                                                        isFull ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                                                    }`}>
                                                        {team.members.length} / {selectedTurno?.TEAMBOWLERS}
                                                    </span>
                                                </div>

                                                {/* Add Members toggle */}
                                                {!isFull && (
                                                    <button
                                                        onClick={() => {
                                                            if (isTarget) {
                                                                setAddTarget(null);
                                                            } else {
                                                                setAddTarget(team.CLAVE);
                                                                setTeamName('');
                                                                setPendingMembers([]);
                                                            }
                                                        }}
                                                        className={`flex-shrink-0 flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition ml-3 ${
                                                            isTarget
                                                                ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                                                                : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                                                        }`}
                                                    >
                                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d={isTarget ? "M6 18L18 6M6 6l12 12" : "M12 4v16m8-8H4"} />
                                                        </svg>
                                                        {isTarget ? 'Done' : 'Add Member'}
                                                    </button>
                                                )}
                                            </div>

                                            {/* Members */}
                                            {team.members.length === 0 ? (
                                                <p className="text-sm text-gray-400 italic">No members yet</p>
                                            ) : (
                                                <div className="space-y-2">
                                                    {team.members.map(m => (
                                                        <div key={m.JUGADOR_CLAVE} className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-2.5">
                                                            <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-xs flex-shrink-0">
                                                                {m.ORDEN}
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <p className="font-semibold text-gray-800 text-sm">{m.NOMBRECOMPLETO}</p>
                                                                <p className="text-xs text-gray-400">TAT# {m.FICHANAL}</p>
                                                            </div>
                                                            <button
                                                                onClick={() => removeMemberFromTeam(team.CLAVE, m.JUGADOR_CLAVE)}
                                                                disabled={memberActionLoading === m.JUGADOR_CLAVE}
                                                                title="Remove from team"
                                                                className="flex-shrink-0 text-gray-400 hover:text-red-500 hover:bg-red-50 p-1.5 rounded-lg transition disabled:opacity-40"
                                                            >
                                                                {memberActionLoading === m.JUGADOR_CLAVE ? (
                                                                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                                                    </svg>
                                                                ) : (
                                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                    </svg>
                                                                )}
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Hint when targeted and not full */}
                                            {isTarget && !isFull && (
                                                <p className="mt-3 text-xs text-indigo-500 flex items-center gap-1.5 bg-indigo-50 rounded-xl px-3 py-2">
                                                    <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                    </svg>
                                                    Click + on the left to add bowlers ({(selectedTurno?.TEAMBOWLERS ?? 0) - team.members.length} spot{(selectedTurno?.TEAMBOWLERS ?? 0) - team.members.length !== 1 ? 's' : ''} remaining)
                                                </p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminManageTeams;
